import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { translate } from '../../lib/translations';

interface Goalie {
  playerid: number;
  goaliefullname: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  wins: number;
  losses: number;
  shutouts: number;
  savepct: number;
  goals: number;
  assists: number;
  isnhl: boolean;
}

interface ProtectedGoaliesTableProps {
  goalies: Goalie[];
  onRemoveFromProtection: (goalie: Goalie) => void;
  language: 'en' | 'fr';
}

interface Column {
  field: keyof Goalie;
  translationKey: string;
  sortable?: boolean;
}

interface SortConfig {
  field: keyof Goalie;
  direction: 'asc' | 'desc';
}

const columns: Column[] = [
  { field: 'goaliefullname', translationKey: 'name', sortable: true },
  { field: 'teamabbrevs', translationKey: 'team' },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'wins', translationKey: 'wins', sortable: true },
  { field: 'losses', translationKey: 'losses', sortable: true },
  { field: 'shutouts', translationKey: 'shutouts', sortable: true },
  { field: 'savepct', translationKey: 'save.percentage', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'salary', translationKey: 'salary', sortable: true },
  { field: 'yearleft', translationKey: 'years.left' },
];

export function ProtectedGoaliesTable({
  goalies,
  onRemoveFromProtection,
  language,
}: ProtectedGoaliesTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'wins',
    direction: 'desc',
  });
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});

  const handleSort = (field: keyof Goalie) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const renderSortIcon = (field: keyof Goalie, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getGoalieImageUrl = (goalie: Goalie) => {
    if (imageErrors[goalie.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${goalie.teamabbrevs}/${goalie.playerid}.png`;
  };

  const sortedGoalies = [...goalies].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Actions</th>
            <th>Photo</th>
            <th>Statut</th>
            {columns.map((column) => (
              <th
                key={column.field}
                onClick={() => column.sortable && handleSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedGoalies.map((goalie) => (
            <tr key={goalie.playerid} className="table-success">
              <td>
                <button
                  className="btn btn-sm btn-danger d-flex align-items-center"
                  onClick={() => onRemoveFromProtection(goalie)}
                >
                  <X size={16} className="me-1" />
                  Retirer
                </button>
              </td>
              <td>
                <img 
                  src={getGoalieImageUrl(goalie)} 
                  alt={goalie.goaliefullname}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  className="border"
                  onError={() => handleImageError(goalie.playerid)}
                />
              </td>
              <td>
                <span
                  className={`badge ${goalie.isnhl ? 'bg-primary' : ''}`}
                  style={goalie.isnhl ? {} : { backgroundColor: '#fd7e14' }}
                >
                  {goalie.isnhl ? translate('main.club', language) : translate('reserves', language)}
                </span>
              </td>
              {columns.map((column) => (
                <td key={`${goalie.playerid}-${column.field}`}>
                  {column.field === 'salary'
                    ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(goalie.salary)
                    : column.field === 'savepct'
                    ? goalie.savepct.toFixed(3)
                    : goalie[column.field]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}